import type { ApprovalDocumentDetail, ApprovalLineDetail } from '../../../types/approval';
import { approvalLines, lineSlotLabel, referrerLines } from '../../../lib/approval-line-slot';

/**
 * 결재 문서 상세 (2026-09-14 · 과제 L3 · `/approval/doc/:id`) — 머리 · 결재선 흐름 · 결재 막대가 같이 쓰는 규칙.
 *
 * 시안 `axi-approval-detail` v2(사용자 확인 — 그대로 반영):
 *   머리 카드(기안자 · 제목 · 상태 · 결재선 n/N) → 탭(본문 · 결재선 · 첨부 · 의견 · 이력) → 하단 결재 막대.
 * ★ 옛 `ApprovalDetail` 은 그대로 둔다 — 문서함 두 열 화면(`/approval/legacy/*`)이 쓴다(회귀 0).
 * ★ 서버에 없는 것은 자리만 그린다 — 아래 `FUTURE_DOC_REQUESTS` 가 그 목록이고 PR 본문 번호와 같다.
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DD HH:mm` — 머리 · 결재 막대. 못 읽으면 빈 글자. */
export function ymdhm(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `MM-DD HH:mm` — 결재선 · 의견(시안 v2). */
export function mdhm(iso: string | null | undefined): string {
  const full = ymdhm(iso);
  return full ? full.slice(5) : '';
}

/** 결재자 이름 — 옛 서버(`approver` 없음)는 번호, 지운 계정은 그 사실을 적는다(`ApprovalDetail` 과 같은 규칙). */
export function approverName(l: ApprovalLineDetail): string {
  if (l.approver === undefined) return `결재자 ${l.approverId}`;
  if (l.approver === null) return '(지워진 계정)';
  return l.approver.name;
}

export type FlowTone = 'ok' | 'turn' | 'rej' | 'skip' | 'wait';

export interface FlowNode {
  key: string;
  /** 기안 · 결재자 · 최종결재자 (합의 줄도 「결재자」다 — 2026-09-25) */
  slot: string;
  name: string;
  /** 직위(있으면) */
  sub: string | null;
  userId: number;
  avatarUrl: string | null;
  tone: FlowTone;
  /** 「승인 · 09-23 10:40」 · 「결재 차례 · 대기 중」 */
  state: string;
  comment: string | null;
  /** 처리 시각 — 의견 탭이 쓴다 */
  at: string | null;
}

/**
 * 결재선 흐름 — 시안 v2 처럼 **기안이 첫 마디**이고, 참고자는 흐름에서 빠진다(아래 명단 · 판 P1 규칙).
 * - 기안자 이름은 상세 응답에 없어(계약 §20.1 은 목록 줄에만) 기준 스냅샷(`basis.drafter`)을 쓰고, 없으면 「기안자」.
 * - 반려 · 회수로 멈춘 문서의 남은 줄은 「진행 안 함」.
 * ⚠ 2026-09-27 — 「합의 줄은 승인/반려가 곧 동의/반대다」는 줄을 **지웠다**. 합의 기능을
 *   다 걷어(#1774 · #1779) **그런 줄이 없다** — 모든 줄이 결재와 같은 말을 쓴다.
 */
export function flowNodes(doc: ApprovalDocumentDetail): FlowNode[] {
  const drafted = doc.status !== 'DRAFT';
  const drafter: FlowNode = {
    key: 'drafter',
    slot: '기안',
    name: doc.basis?.drafter.name ?? '기안자',
    sub: doc.basis?.drafter.position ?? null,
    userId: Number(doc.drafterId) || 0,
    avatarUrl: null,
    tone: drafted ? 'ok' : 'wait',
    state: drafted ? `상신${doc.draftedAt ? ` · ${mdhm(doc.draftedAt)}` : ''}` : '임시보관 · 아직 상신 전',
    comment: null,
    at: doc.draftedAt,
  };
  const live = doc.status === 'IN_PROGRESS' || doc.status === 'DRAFT';
  const lines = approvalLines(doc.lines);
  return [drafter, ...lines.map((l, i, arr): FlowNode => {
    /*
     * ★★★ **합의 줄과 결재 줄은 이제 «자리 이름»이 같다**(둘 다 「결재자」 · 2026-09-25 PR-2).
     *   ⇒ 그래서 **가르는 짐이 전부 이 `state` 글자에 얹혀 있다.**
     *   ⛔ `agree` 가지를 지우거나 두 쪽 글자를 같게 만들지 말 것 — 그러면 「안 막는 줄」이
     *     「막는 줄」과 **똑같이** 읽히고, 사람이 「저 사람이 안 눌러서 멈췄다」로 본다.
     *     `approval-flow-words.test.ts` 가 그것을 잰다.
     */
    const turn = l.status === 'WAITING' && doc.status === 'IN_PROGRESS' && l.step === doc.currentStep;
    const when = mdhm(l.processedAt);
    const at = when ? ` · ${when}` : '';
    let tone: FlowTone;
    let state: string;
    /*
      ⛔ 합의 전용 글자(「동의 · 반대 · 합의 요청」)를 지웠다 (2026-09-27 · 합의 기능 삭제).
        종전에는 «같은 줄»을 종류에 따라 다른 말로 적어, 「막는 줄」과 「안 막는 줄」을
        이 글자가 갈랐다. 그 줄이 없어져 가를 것도 없다.
    */
    if (l.status === 'APPROVED') { tone = 'ok'; state = `승인${at}`; }
    else if (l.status === 'REJECTED') { tone = 'rej'; state = `반려${at}`; }
    else if (l.status === 'SKIPPED') { tone = 'skip'; state = '진행 안 함'; }
    else if (turn) { tone = 'turn'; state = '결재 차례 · 대기 중'; }
    else if (live) { tone = 'wait'; state = '대기 · 예정'; }
    else { tone = 'skip'; state = '진행 안 함'; }
    return {
      key: String(l.id),
      slot: lineSlotLabel(arr, i),
      name: approverName(l),
      sub: l.approver?.position ?? null,
      userId: Number(l.approver?.userId ?? l.approverId) || 0,
      avatarUrl: l.approver?.avatarUrl ?? null,
      tone,
      state,
      comment: l.comment,
      at: l.processedAt,
    };
  })];
}

export interface RefNode { key: string; name: string; userId: number; avatarUrl: string | null }

/** 참고자 명단 — 결재하지 않고 볼 수만 있는 사람(판 P1 · 흐름 아래 이름만). */
export function referrerNodes(doc: ApprovalDocumentDetail): RefNode[] {
  return referrerLines(doc.lines).map((l) => ({
    key: String(l.id),
    name: approverName(l),
    userId: Number(l.approver?.userId ?? l.approverId) || 0,
    avatarUrl: l.approver?.avatarUrl ?? null,
  }));
}

/** 머리 「결재선 n / N」 — 결재 · 합의 줄 중 처리된 수(참고자 제외). 줄이 없는 옛 응답은 단계 수로. */
export function lineProgress(doc: ApprovalDocumentDetail): { done: number; total: number } {
  const lines = approvalLines(doc.lines);
  if (lines.length === 0) {
    const total = Math.max(1, doc.totalSteps);
    return { done: doc.status === 'APPROVED' ? total : Math.max(0, Math.min(total, doc.currentStep - 1)), total };
  }
  // ★ 리뷰 #1272 — 「처리됨」은 승인 · 반려만. 건너뜀(SKIPPED · 회수 · 전결 뒤 줄)은 처리한 것이 아니라 세지 않는다
  //   (종전 `!== 'WAITING'` 은 회수 문서를 「결재선 2/2」 로 가득 채웠다).
  return { done: lines.filter((l) => l.status === 'APPROVED' || l.status === 'REJECTED').length, total: lines.length };
}

// 상태 배지는 lib/approval-status.ts 한 벌로 옮겼다(2026-10-18 · web 레인 W11b) — 옛 docBadge() 는 부르는 곳이 0 이라 지웠다.

export type DocTab = 'body' | 'line' | 'files' | 'talk' | 'log';

export const DOC_TABS: ReadonlyArray<{ key: DocTab; label: string }> = [
  { key: 'body', label: '본문' },
  { key: 'line', label: '결재선' },
  { key: 'files', label: '첨부' },
  { key: 'talk', label: '의견' },
  { key: 'log', label: '이력' },
];

/**
 * 후속 서버 요청 — 시안에는 그렸고 서버에 없는 것(PR 본문 번호와 같다).
 * ★ 화면은 이 자리들을 잠긴 단추 · 「데이터 준비 중」 으로 그린다 — 값을 지어내지 않는다.
 */
export const FUTURE_DOC_REQUESTS = [
  '① 보류 — 결재 막대 [보류] · 상태 · 라우트 없음',
  '② 재알림 보내기 — 진행 중 문서의 대기자에게 다시 알림',
  '③ 문서 PDF · PDF 받기 — 지금은 브라우저 인쇄만',
  '④ 의견 스레드 · 「기안자에게만 보이기」 — 지금 의견은 승인 · 반려 · 동의 · 반대에 붙는 한 줄(결재선 줄 comment)',
  '⑤ 상세 응답의 기안자 사진 · 이름 · 팀 — 목록 줄 `drafter`(§20.1)는 있고 상세에는 없다(지금은 기준 스냅샷 `basis.drafter`)',
  '⑥ 문서 보존 기간',
  '⑦ 다시 기안 — 반려 문서를 새 기안으로 이어 쓰기(새 기안 화면이 문서를 받아 채우지 않는다)',
] as const;
